export type AdminRole = 'reception' | 'assistant_doctor' | 'doctor' | 'pharmacy';

export interface AdminNavItem {
  title: string;
  path?: string;
  icon?: string;
  items?: AdminNavItem[];
}

export interface AdminNavConfig {
  role: AdminRole;
  label: string;
  allowedPaths: string[];
  menus: AdminNavItem[];
}

const receptionConfig: AdminNavConfig = {
  role: 'reception',
  label: 'Reception',
  allowedPaths: [
    '/admin/dashboard',
    '/admin/appointment',
    '/admin/appointment-list',
    '/admin/patient-registration',
    '/admin/pending-registrations',
    '/admin/cards',
    '/admin/medicine',
    '/admin/admin-inventory',
  ],
  menus: [
    {
      title: 'Appointments',
      icon: 'pi-calendar',
      items: [
        { title: 'Calendar', path: '/admin/appointment', icon: 'pi-calendar' },
        { title: 'Appointment list', path: '/admin/appointment-list', icon: 'pi-list' },
      ],
    },
    {
      title: 'Patients',
      icon: 'pi-users',
      items: [
        { title: 'Registration', path: '/admin/patient-registration', icon: 'pi-user-plus' },
        { title: 'Self registrations', path: '/admin/pending-registrations', icon: 'pi-inbox' },
        { title: 'Cards', path: '/admin/cards', icon: 'pi-id-card' },
      ],
    },
    {
      title: 'Pharmacy',
      icon: 'pi-box',
      items: [
        { title: 'Medicines', path: '/admin/medicine', icon: 'pi-briefcase' },
        { title: 'Inventory', path: '/admin/admin-inventory', icon: 'pi-database' },
      ],
    },
  ],
};

const assistantDoctorConfig: AdminNavConfig = {
  role: 'assistant_doctor',
  label: 'Assistant Doctor',
  allowedPaths: [
    '/admin/dashboard',
    '/admin/pre-consultation',
    '/admin/patient-registration',
  ],
  menus: [
    {
      title: 'Patients',
      icon: 'pi-users',
      items: [
        { title: 'Pre-consultation', path: '/admin/pre-consultation', icon: 'pi-heart' },
        { title: 'Registration', path: '/admin/patient-registration', icon: 'pi-user-plus' },
      ],
    },
  ],
};

const doctorConfig: AdminNavConfig = {
  role: 'doctor',
  label: 'Doctor',
  allowedPaths: [
    '/admin/dashboard',
    '/admin/appointment',
    '/admin/appointment-list',
    '/admin/prescriptions',
    '/admin/patient-registration',
    '/admin/pending-registrations',
    '/admin/cards',
    '/admin/medicine',
    '/admin/admin-inventory',
    '/admin/certificates',
    '/admin/analytics-collections',
  ],
  menus: [
    {
      title: 'Consult',
      icon: 'pi-file-edit',
      path: '/admin/prescriptions',
    },
    {
      title: 'Appointments',
      icon: 'pi-calendar',
      items: [
        { title: 'Calendar', path: '/admin/appointment', icon: 'pi-calendar' },
        { title: 'Appointment list', path: '/admin/appointment-list', icon: 'pi-list' },
      ],
    },
    {
      title: 'Patients',
      icon: 'pi-users',
      items: [
        { title: 'Registration', path: '/admin/patient-registration', icon: 'pi-user-plus' },
        { title: 'Self registrations', path: '/admin/pending-registrations', icon: 'pi-inbox' },
        { title: 'Cards', path: '/admin/cards', icon: 'pi-id-card' },
      ],
    },
    {
      title: 'Pharmacy',
      icon: 'pi-box',
      items: [
        { title: 'Medicines', path: '/admin/medicine', icon: 'pi-briefcase' },
        { title: 'Inventory', path: '/admin/admin-inventory', icon: 'pi-database' },
      ],
    },
    {
      title: 'Certificates',
      icon: 'pi-verified',
      path: '/admin/certificates',
    },
    {
      title: 'Reports',
      icon: 'pi-chart-bar',
      path: '/admin/analytics-collections',
    },
  ],
};

const pharmacyConfig: AdminNavConfig = {
  role: 'pharmacy',
  label: 'Pharmacy',
  allowedPaths: [
    '/admin/dashboard',
    '/admin/pharmacy',
    '/admin/medicine',
  ],
  menus: [
    {
      title: 'Dispense',
      icon: 'pi-shopping-bag',
      path: '/admin/pharmacy',
    },
    {
      title: 'Medicines',
      icon: 'pi-briefcase',
      path: '/admin/medicine',
    },
  ],
};

const CONFIG_BY_ROLE: Record<AdminRole, AdminNavConfig> = {
  reception: receptionConfig,
  assistant_doctor: assistantDoctorConfig,
  doctor: doctorConfig,
  pharmacy: pharmacyConfig,
};

export function isAdminRole(value: unknown): value is AdminRole {
  return value === 'reception'
    || value === 'assistant_doctor'
    || value === 'doctor'
    || value === 'pharmacy';
}

export function getAdminNavConfig(role: unknown): AdminNavConfig | null {
  if (!isAdminRole(role)) {
    return null;
  }
  return CONFIG_BY_ROLE[role];
}

export function isPathAllowed(allowedPaths: string[], url: string): boolean {
  const pathOnly = (url || '').split('?')[0];
  return allowedPaths.some((allowed) => pathOnly === allowed || pathOnly.startsWith(`${allowed}/`));
}

export function menusForWireFrame(menus: AdminNavItem[]): any[] {
  return menus.map((menu) => ({
    title: menu.title,
    icon: 'home',
    isActive: true,
    path: menu.path,
    items: menu.items?.map((item) => ({
      title: item.title,
      icon: 'home',
      path: item.path,
    })),
  }));
}
